'use client';
import { sendGAEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import { InfoIcon } from '@/app/components/icons/InfoIcon';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { trackPayButtonClick } from '@/app/components/shared/RedditPixel';
import { trackInitiateCheckout } from '@/app/components/shared/TikTokPixel';
import { Button } from '@/app/components/ui/button';
import { useGlobalModal, usePayment, usePaymentIntent } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { CreditV1PeriodProduct, SubscriptionProductType } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { TEAM_BUTTON_BG_STYLE } from '../constants';
import { ConsultButton } from './ConsultButton';

type TeamSubscriptionButtonProps = {
  loading: boolean;
  product: SubscriptionProductType;
  source?: string;
  /** 弹窗来源：source==='dialog' 时随 vip_price_click 上报为 modal_from_page，标识由哪个入口打开套餐弹窗 */
  modalFromPage?: string;
  /** 仅查看不可购买。例如企业成员（role=member）在团队空间查看团队套餐。 */
  purchaseDisabled?: boolean;
};

const DISABLED_CLASS = 'h-12 w-full bg-system-white12 text-sm text-system-white36 hover:bg-system-white12';
type TeamBundleUnavailableReason = Exclude<NonNullable<CreditV1PeriodProduct['unavailable_reason']>, 'unspecified'>;

const isTeamBundleUnavailableReason = (
  value: CreditV1PeriodProduct['unavailable_reason'],
): value is TeamBundleUnavailableReason => Boolean(value && value !== 'unspecified');

const TEAM_BUNDLE_UNAVAILABLE_REASON_KEYS = {
  members_exceed_seat_limit: 'payments.plans.teamBundleUnavailableReasonMembersExceedSeatLimit',
  team_not_expired: 'payments.plans.teamBundleUnavailableReasonTeamNotExpired',
  not_team_admin: 'payments.plans.memberReadonlyTooltip',
  product_not_configured: 'payments.plans.teamBundleUnavailableReasonProductNotConfigured',
} satisfies Record<TeamBundleUnavailableReason, string>;

/**
 * 企业版按"次包/新购"语义，product_pay_status 由 buildTeamProductMap 统一派生：
 * - `current`：当前持有的 tier + billing_period，显示"当前套餐"且禁用；
 * - `unavailable`：已持有团队 bundle / 后端标记不可购买，禁用"立即购买"；
 * - `available`：可购买，显示"立即购买"。
 * 只读（企业成员）单独走 purchaseDisabled 分支，禁用 + tooltip。
 */
export const TeamSubscriptionButton = ({
  loading,
  product,
  source = '',
  modalFromPage,
  purchaseDisabled = false,
}: TeamSubscriptionButtonProps) => {
  const { userInfo, payStatus } = useStore((state) => ({
    userInfo: state.userInfo,
    payStatus: state.payStatus,
  }));
  const { pay } = usePayment();
  const { savePaymentIntent } = usePaymentIntent();
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const t = useTranslations();
  const pathname = usePathname();
  const unavailableReason = isTeamBundleUnavailableReason(product.unavailable_reason)
    ? product.unavailable_reason
    : undefined;
  const unavailableReasonKey = unavailableReason ? TEAM_BUNDLE_UNAVAILABLE_REASON_KEYS[unavailableReason] : undefined;

  const handleClickPayment = () => {
    sendGAEvent('event', 'subscription_button_click', {
      subscribe_type: `${product.product_type}-${product.billing_period}`,
      vip_type: product.plan_type,
      page_type: source === 'dialog' ? 'dialog_box' : 'page',
    });

    trackInitiateCheckout();
    trackPayButtonClick();

    if (!userInfo) {
      savePaymentIntent({ productId: product.id, timestamp: Date.now() });
      openLoginDialog();
      reportEvent('login_pop_exposure', {
        refer_element: 'vip-price-modal',
        refer_page: pathname,
      });
      return;
    }

    pay(product);

    const isDialog = source === 'dialog';
    reportEvent('vip_price_click', {
      from_page: isDialog ? 'vip_price_modal_exposure' : 'vip_price_exposure',
      subscribe_type: `${product.product_type}-${product.billing_period}`,
      vip_type: product.plan_type,
      ...(isDialog ? { modal_from_page: modalFromPage } : {}),
    });
  };

  if (product.product_pay_status === 'current') {
    return (
      <Button className="h-12 w-full bg-system-white12 font-normal text-sm hover:bg-system-white24" disabled>
        {t('payments.plans.currentText')}
      </Button>
    );
  }

  if (purchaseDisabled) {
    return (
      <CustomTooltip
        side="bottom"
        content={t('payments.plans.memberReadonlyTooltip')}
        trigger="auto"
        disableHoverableContent={true}
      >
        <Button className={DISABLED_CLASS} disabled>
          {t('payments.plans.passButton')}
          <InfoIcon className="ml-2 h-4 w-4 shrink-0" />
        </Button>
      </CustomTooltip>
    );
  }

  if (product.product_pay_status === 'unavailable') {
    if (unavailableReasonKey) {
      return (
        <CustomTooltip side="bottom" content={t(unavailableReasonKey)} trigger="auto" disableHoverableContent={true}>
          <Button className={DISABLED_CLASS} disabled>
            {t('payments.plans.passButton')}
            <InfoIcon className="ml-2 h-4 w-4 shrink-0" />
          </Button>
        </CustomTooltip>
      );
    }

    return (
      <Button className={DISABLED_CLASS} disabled>
        {t('payments.plans.passButton')}
      </Button>
    );
  }

  if (product.product_pay_status === 'available') {
    const isLoading = loading || payStatus === 'loading';
    return (
      <Button
        className="group relative h-12 w-full rounded-10 border-none font-medium text-sm text-white"
        style={TEAM_BUTTON_BG_STYLE}
        data-agl-cvt="7"
        disabled={isLoading}
        onClick={handleClickPayment}
      >
        {isLoading && <Loading className="animate-spin text-base" />}
        <span className="relative z-10">{t('payments.plans.passButton')}</span>
      </Button>
    );
  }

  return <ConsultButton />;
};
